import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Package, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ZONE_LABELS = {
  ES_PENINSULA: 'España Peninsular',
  ES_BALEARES: 'Islas Baleares',
  ES_CANARIAS: 'Islas Canarias',
  PT: 'Portugal',
  EU: 'Unión Europea',
  WORLD: 'Resto del mundo',
};

export default function ShippingInfo() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/shipping/rates`);
        if (res.ok) {
          const data = await res.json();
          setZones(Array.isArray(data) ? data : data.zones || []);
        }
      } catch (e) {
        console.error('Error loading shipping rates:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-black text-white py-10 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to="/" className="text-gray-300 hover:text-white text-sm inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            Envíos y devoluciones
          </h1>
          <p className="text-gray-400 mt-2 text-sm">Tarifas actualizadas automáticamente</p>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6 py-12 space-y-14" data-testid="shipping-info-page">
        {/* Shipping rates */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Truck className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Tarifas de envío</h2>
          </div>
          {loading ? (
            <p className="text-gray-500">Cargando tarifas…</p>
          ) : zones.length === 0 ? (
            <p className="text-gray-500">
              Las tarifas se calculan dinámicamente en el checkout en función de tu destino
              y del importe de la compra. Verás el coste exacto antes de confirmar el pedido.
            </p>
          ) : (
            <div className="space-y-4" data-testid="shipping-zones-list">
              {zones.map((z) => (
                <div key={z.zone_code || z.code || z.zone} className="border border-gray-200 p-4 rounded">
                  <h3 className="font-bold text-lg mb-1">{z.zone_label || ZONE_LABELS[z.zone_code] || z.zone_code || z.zone}</h3>
                  {Array.isArray(z.tiers) && z.tiers.length === 1 && z.tiers[0].label && (
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                      {z.tiers[0].label}
                    </p>
                  )}
                  {z.free_from != null && (
                    <p className="text-sm text-green-700 font-semibold mb-2">
                      Envío gratis a partir de {Number(z.free_from).toFixed(2)}€
                    </p>
                  )}
                  {Array.isArray(z.tiers) && z.tiers.length > 1 && (
                    <table className="w-full text-sm mt-2">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-2 border-b">Importe del pedido</th>
                          <th className="text-right p-2 border-b">Coste</th>
                        </tr>
                      </thead>
                      <tbody>
                        {z.tiers.map((t, i) => (
                          <tr key={`tier-${t.min_subtotal ?? 'x'}-${i}`} className="border-b last:border-0">
                            <td className="p-2 text-gray-700">
                              {Number(t.min_subtotal || 0).toFixed(2)}€
                              {t.max_subtotal != null ? ` - ${Number(t.max_subtotal).toFixed(2)}€` : ' o más'}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {Number(t.shipping_cost || 0) === 0 ? 'GRATIS' : `${Number(t.shipping_cost).toFixed(2)}€`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {Array.isArray(z.tiers) && z.tiers.length === 1 && (
                    <p className="text-2xl font-bold mt-2" data-testid={`flat-rate-${z.zone || z.zone_code}`}>
                      {Number(z.tiers[0].shipping_cost || 0) === 0 ? 'GRATIS' : `${Number(z.tiers[0].shipping_cost).toFixed(2)}€`}
                      <span className="text-sm text-gray-500 font-normal ml-2">tarifa única</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-sm text-gray-500 mt-4">
            Los envíos se preparan en un plazo de <strong>24-48 horas laborables</strong> desde la
            confirmación del pago. Los tiempos de tránsito varían según el destino.
          </p>
        </section>

        {/* Returns */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <RotateCcw className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Devoluciones</h2>
          </div>
          <div className="space-y-3 text-gray-700">
            <p>
              Dispones de <strong>14 días naturales</strong> desde la recepción para desistir
              del pedido (art. 102 LGDCU), en su embalaje original y sin uso.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Error nuestro:</strong> sin coste. Te enviamos etiqueta prepagada.
              </li>
              <li>
                <strong>Tú nos envías la mercancía:</strong> sin cargos adicionales por nuestra parte.
              </li>
              <li>
                <strong>Recogida / etiqueta prepagada por nosotros:</strong> 5,50€ que se
                descuentan del reembolso.
              </li>
            </ul>
            <p>
              <strong>Reembolso:</strong> 24-72 horas tras recibir y verificar la mercancía,
              al mismo medio de pago utilizado en la compra.
            </p>
          </div>
        </section>

        {/* Exchanges */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Cambios</h2>
          </div>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Gastos del nuevo envío: <strong>4,50€</strong>.</li>
            <li>Si nos mandas tú la pieza original: solo pagas los 4,50€ del nuevo envío.</li>
            <li>Si recogemos en tu domicilio: <strong>5,50€</strong> adicionales (total 10€).</li>
          </ul>
        </section>

        <section>
          <p className="text-sm text-gray-600 border-t pt-6">
            Para más detalle legal consulta nuestras{' '}
            <Link to="/condiciones" className="text-black underline">
              Condiciones Generales de Venta
            </Link>.
          </p>
        </section>
      </div>
    </div>
  );
}
